import { useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound, Outlet } from "@tanstack/react-router";
import {
  BookOpen,
  Calendar,
  FolderOpen,
  LayoutDashboard,
  MessageCircle,
  ReceiptText,
  StickyNote,
  Target,
} from "lucide-react";
import { z } from "zod";

import type { WorkspaceNavGroup } from "~/components/engagement/workspace-shell";
import { EngagementWorkspaceShell } from "~/components/engagement/workspace-shell";
import { Badge } from "~/components/ui/badge";
import { ButtonRouter } from "~/components/ui/button";
import { cn } from "~/lib/cn";
import { EngagementStatus, STATUS_CHIP_STYLES } from "~/lib/engagement-status";
import {
  useCoachInfoForEngagement,
  coachInfoForEngagementQueryOptions,
} from "~/lib/queries/engagementQuery";
import { useHomeworkList } from "~/lib/queries/homeworkQuery";
import { engagementConversationQueryOptions } from "~/lib/queries/messagingQuery";
import { TaskStatus } from "~/lib/schemas/db-enums";

const engagementSearchSchema = z.object({
  homeworkId: z.string().optional(),
});

export const Route = createFileRoute("/_user/coaching/$engagementId")({
  staticData: { title: "Coaching Program" },
  validateSearch: engagementSearchSchema,
  loader: async ({ params, context }) => {
    const coachInfo = await context.queryClient.ensureQueryData(
      coachInfoForEngagementQueryOptions(params.engagementId),
    );
    // Missing / not-yours engagement is an expected miss (stale bookmark,
    // forwarded link, deleted engagement) — render notFound rather than crash.
    if (!coachInfo) throw notFound();
    return { title: coachInfo.name };
  },
  notFoundComponent: EngagementNotFound,
  component: RouteComponent,
});

function EngagementNotFound() {
  return (
    <div className="mx-auto max-w-xl py-16 text-center">
      <h1 className="font-heading text-2xl font-medium">
        Coaching program not found
      </h1>
      <p className="mt-2 text-gray-600">
        This coaching program doesn't exist anymore or isn't one of yours.
      </p>
      <ButtonRouter
        to="/coaching"
        variant="ghost"
        className="mt-6 text-purple-600 hover:bg-purple-50 hover:text-purple-700"
      >
        Back to my coaching team
      </ButtonRouter>
    </div>
  );
}

function RouteComponent() {
  const { engagementId } = Route.useParams();

  const { data: coachInfo, isLoading: coachInfoLoading } =
    useCoachInfoForEngagement(engagementId);
  const { data: homeworkData } = useHomeworkList(engagementId);
  const { data: conversation } = useQuery(
    engagementConversationQueryOptions(engagementId),
  );

  const pendingHomeworkCount =
    homeworkData?.homeworks.filter((h) => h.status === TaskStatus.ACTIVE)
      .length ?? 0;

  const status = (coachInfo?.status ||
    EngagementStatus.ACTIVE) as EngagementStatus;

  const navGroups: WorkspaceNavGroup[] = [
    {
      label: "My coaching team",
      items: [
        {
          id: "overview",
          label: "Overview",
          to: "/coaching/$engagementId",
          icon: LayoutDashboard,
          exact: true,
        },
        {
          id: "sessions",
          label: "Sessions",
          to: "/coaching/$engagementId/sessions",
          icon: Calendar,
        },
        {
          id: "messages",
          label: "Messages",
          to: "/coaching/$engagementId/messages",
          icon: MessageCircle,
          badge: conversation?.unreadCount,
        },
        {
          id: "homework",
          label: "Actions",
          to: "/coaching/$engagementId/homework",
          icon: BookOpen,
          badge: pendingHomeworkCount,
        },
        {
          id: "resources",
          label: "Resources",
          to: "/coaching/$engagementId/coaching-resources",
          icon: FolderOpen,
        },
      ],
    },
    {
      label: "Reference",
      items: [
        {
          id: "goals",
          label: "Goals",
          to: "/coaching/$engagementId/coaching-goals-and-outcomes",
          icon: Target,
        },
        {
          id: "notes",
          label: "Notes",
          to: "/coaching/$engagementId/notes",
          icon: StickyNote,
        },
        {
          id: "admin",
          label: "Billing & Documents",
          to: "/coaching/$engagementId/admin",
          icon: ReceiptText,
        },
      ],
    },
  ];

  return (
    <EngagementWorkspaceShell
      engagementId={engagementId}
      backTo="/coaching"
      backLabel="Back to your coaching"
      kicker="Your coach"
      name={coachInfo?.name || "Coach"}
      avatarUrl={coachInfo?.profilePictureUrl}
      headerLoading={coachInfoLoading}
      statusSlot={
        <Badge className={cn("capitalize", STATUS_CHIP_STYLES[status])}>
          {status}
        </Badge>
      }
      actions={
        <ButtonRouter
          to="/coaching/$engagementId/messages"
          params={{ engagementId }}
          className="gap-2"
        >
          <MessageCircle className="size-4" />
          Message coach
        </ButtonRouter>
      }
      navGroups={navGroups}
    >
      <Outlet />
    </EngagementWorkspaceShell>
  );
}
